import { getAuth, getRedirectResult, onIdTokenChanged } from 'firebase/auth'
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore'

/**
 * Singleton promise that resolves when Firebase Auth is fully ready
 * with a valid ID token for Firestore operations.
 *
 * Also handles the result from Google redirect sign-in if the user
 * was redirected back from Google.
 *
 * NOTE: Due to Firebase SDK internals, an initial "permission-denied" error
 * may still appear in the console during page load. This is benign - the SDK
 * automatically retries and succeeds. The error cannot be prevented without
 * significant delays that would hurt UX.
 */
let authReadyPromise: Promise<void> | null = null
let isAuthReady = false
let redirectUser: ReturnType<typeof getAuth>['currentUser'] | null = null
let rollingRenewalInstalled = false
let lastRollingRenewalAt = 0

/**
 * Attach a global onIdTokenChanged listener that re-mints the server session
 * cookie whenever Firebase refreshes the ID token. This keeps the 14-day
 * session cookie window rolling for active users so it never silently
 * expires while the client still thinks it's signed in.
 *
 * Throttled to at most one renewal per SESSION_RENEWAL_MIN_INTERVAL_MS, and
 * only renews when the stored expiry is within SESSION_REFRESH_MARGIN_MS.
 */
function installRollingSessionRenewal(auth: ReturnType<typeof getAuth>): void {
  if (rollingRenewalInstalled) return
  rollingRenewalInstalled = true

  onIdTokenChanged(auth, async (user) => {
    if (!user) return

    const now = Date.now()
    if (now - lastRollingRenewalAt < SESSION_RENEWAL_MIN_INTERVAL_MS) return
    if (!isSessionDueForRefresh(now)) return

    lastRollingRenewalAt = now
    try {
      const idToken = await user.getIdToken(false)
      const res = await $fetch<{ success: boolean, expiresAt?: number }>('/api/auth/create-session', {
        method: 'POST',
        body: {
          idToken,
          expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days
        },
      })
      if (res?.expiresAt) setStoredSessionExpiresAt(res.expiresAt)
    }
    catch (error) {
      console.error('[Auth] Rolling session renewal failed:', error)
    }
  })
}

/**
 * If we just completed a redirect sign-in, return the user we got from the redirect.
 * This avoids race conditions with vuefire's getCurrentUser which may not be aware
 * of the redirect result yet.
 */
export function getRedirectUserIfPresent(): ReturnType<typeof getAuth>['currentUser'] {
  return redirectUser
}

/**
 * Handle Google redirect result if user came from a redirect flow
 * This must be called early before other auth operations
 * Returns the user if one was signed in via redirect
 */
// Returns the awaited credential, so the annotation must unwrap
// getRedirectResult's own promise rather than nest inside another one.
async function handleRedirectIfNeeded(auth: ReturnType<typeof getAuth>): Promise<Awaited<ReturnType<typeof getRedirectResult>>> {
  try {
    const result = await getRedirectResult(auth)

    if (result?.user) {
      // Create session for the newly signed-in user
      try {
        const idToken = await result.user.getIdToken(true)
        const sessionRes = await $fetch<{ success: boolean, expiresAt?: number }>('/api/auth/create-session', {
          method: 'POST',
          body: {
            idToken,
            expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days in milliseconds
          },
        })
        if (sessionRes?.expiresAt) setStoredSessionExpiresAt(sessionRes.expiresAt)
      }
      catch (sessionError) {
        console.error('[Auth] Failed to create session after redirect:', sessionError)
      }

      // Ensure member document exists
      try {
        const db = getFirestore()
        const docRef = doc(db, 'members', result.user.uid)
        const docSnap = await getDoc(docRef)

        if (!docSnap.exists()) {
          await setDoc(docRef, {
            email: result.user.email,
            created_at: new Date(),
            updated_at: new Date(),
            member_type: 'pending',
            name: result.user.displayName || '',
          })
        }
      }
      catch (memberError) {
        console.error('[Auth] Failed to create member doc after redirect:', memberError)
      }

      redirectUser = result.user

      // Don't navigate here - let the middleware handle it after auth is ready
      // This ensures the auth flow completes properly on all browsers
      return result
    }
    return null
  }
  catch (error) {
    // Log but don't throw - user may not have come from a redirect
    console.error('[Auth] Redirect result error:', error)
    return null
  }
}

/**
 * Returns a promise that resolves when Firebase Auth token is available.
 */
export function useAuthReady(): Promise<void> {
  if (isAuthReady) {
    return Promise.resolve()
  }

  if (authReadyPromise) {
    return authReadyPromise
  }

  authReadyPromise = new Promise((resolve) => {
    const auth = getAuth()

    // Install rolling session cookie renewal (idempotent).
    installRollingSessionRenewal(auth)

    // Always check for redirect result first, even if user appears to be signed in
    // This handles the case where Firebase restored auth state from cache
    // but we still need to process the redirect result for proper navigation
    handleRedirectIfNeeded(auth).then((redirectResult) => {
      // If we got a redirect result, auth is ready
      if (redirectResult?.user) {
        isAuthReady = true
        resolve()
        return
      }

      // No redirect result - check current auth state
      if (auth.currentUser) {
        // User is already signed in - just ensure we have a valid token
        auth.currentUser.getIdToken(false).then(() => {
          isAuthReady = true
          resolve()
        }).catch(() => {
          // Token might be expired, try force refresh
          auth.currentUser?.getIdToken(true).then(() => {
            isAuthReady = true
            resolve()
          }).catch(() => {
            isAuthReady = true
            resolve()
          })
        })
        return
      }

      // No current user - wait for auth state to load.
      //
      // The observer can fire synchronously, before onIdTokenChanged has
      // returned. Referencing a `const unsubscribe` from inside the callback
      // then threw `Cannot access 'unsubscribe' before initialization`, which
      // aborted the callback and left this promise permanently unsettled — the
      // app would wait on auth forever. Settle first, unsubscribe whenever the
      // handle exists.
      let unsubscribe: (() => void) | null = null
      let settled = false

      const settle = () => {
        isAuthReady = true
        settled = true
        if (unsubscribe) unsubscribe()
        resolve()
      }

      unsubscribe = onIdTokenChanged(auth, async (user) => {
        if (user) {
          // Ensure token is obtained (don't force refresh on initial load)
          await user.getIdToken(false)
        }
        settle()
      })

      // Fired synchronously: `unsubscribe` was still null inside settle(), so
      // tear the observer down now that the handle exists.
      if (settled) unsubscribe()
    })
  })

  return authReadyPromise
}

/**
 * Reset the auth ready state (call on logout)
 */
export function resetAuthReady(): void {
  isAuthReady = false
  authReadyPromise = null
  redirectUser = null
  lastRollingRenewalAt = 0
}
